import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { keyframes, Global, css } from '@emotion/react';
import SocialLinks from '../components/SocialLinks';
import { trackClick, trackEvent } from '../utils/analytics';

// Global scrollbar styles
const GlobalScrollbarStyles = css`
  /* Webkit browsers (Chrome, Safari, Edge) */
  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.8);
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 0, 0.2);
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 0, 0.8) 0%,
      rgba(255, 255, 0, 0.6) 50%,
      rgba(255, 255, 0, 0.8) 100%
    );
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 0, 0.4);
    box-shadow: 
      0 0 6px rgba(255, 255, 0, 0.3),
      inset 0 0 6px rgba(255, 255, 0, 0.2);
    transition: all 0.3s ease;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 0, 1) 0%,
      rgba(255, 255, 0, 0.8) 50%,
      rgba(255, 255, 0, 1) 100%
    );
    box-shadow: 
      0 0 10px rgba(255, 255, 0, 0.5),
      inset 0 0 6px rgba(255, 255, 0, 0.3);
  }

  ::-webkit-scrollbar-corner {
    background: rgba(0, 0, 0, 0.8);
  }

  /* Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 0, 0.7) rgba(0, 0, 0, 0.8);
  }

  /* Mobile scrollbar for brutal site */
  .mobile-brutal-site {
    scrollbar-width: thin;
    scrollbar-color: #666666 #ffffff;
  }

  .mobile-brutal-site::-webkit-scrollbar {
    width: 8px;
  }

  .mobile-brutal-site::-webkit-scrollbar-track {
    background: #f1f1f1;
    border: 1px solid #cccccc;
  }

  .mobile-brutal-site::-webkit-scrollbar-thumb {
    background: #666666;
    border-radius: 4px;
  }

  .mobile-brutal-site::-webkit-scrollbar-thumb:hover {
    background: #333333;
  }
`;

const scanlineAnimation = keyframes`
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(50%);
  }
`;

const vhsTrackingAnimation = keyframes`
  0% {
    transform: translateY(0);
    opacity: 0.15;
  }
  50% {
    transform: translateY(-15px);
    opacity: 0.05;
  }
  100% {
    transform: translateY(0);
    opacity: 0.15;
  }
`;

const noiseAnimation = keyframes`
  0%, 100% { transform: translate(0, 0); }
  10% { transform: translate(-1px, -1px); }
  20% { transform: translate(1px, 1px); }
  30% { transform: translate(-1px, 1px); }
  40% { transform: translate(1px, -1px); }
  50% { transform: translate(-1px, 0px); }
  60% { transform: translate(1px, 0px); }
  70% { transform: translate(0px, 1px); }
  80% { transform: translate(0px, -1px); }
  90% { transform: translate(-1px, -1px); }
`;

const CyberpunkRoom = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background: black;
  min-width: 1200px;

  @media (max-width: 768px) {
    height: 100%;
    min-height: 100vh;
    overflow: hidden;
    min-width: unset;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const MainContainer = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: black;
  z-index: 0;
  animation: ${fadeIn} 0.001s linear 0.08s both;

  @media (max-width: 768px) {
    inset: 0;
  }
`;

const VideoContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: black;
  z-index: 1;
  contain: layout style paint;

  @media (max-width: 768px) {
    height: 100%;
    min-height: 100vh;
  }
`;

const BackgroundBlur = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
`;

const BlurredVideo = styled.video`
  position: absolute;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1.1);
  object-fit: cover;
  filter: blur(10px);
  opacity: 0.6;
  will-change: transform;
  backface-visibility: hidden;
`;

const ContentWrapper = styled.div`
  position: relative;
  width: 1920px !important;
  height: 1080px !important;
  max-width: 1920px !important;
  max-height: 1080px !important;
  overflow: hidden !important;
  border-radius: 32px;
  margin: 0 auto;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.7);
  contain: layout style paint;

  @media (max-width: 768px) {
    border-radius: 0;
    width: 100vw !important;
    height: 100vh !important;
  }
`;

const BackgroundVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 1920px !important;
  height: 1080px !important;
  max-width: 1920px !important;
  max-height: 1080px !important;
  object-fit: none;
  z-index: 0;
  will-change: transform;
  backface-visibility: hidden;
  display: block;

  @media (max-width: 768px) {
    width: 100vw !important;
    height: 100vh !important;
    object-fit: cover;
  }
`;

const InteractiveArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    padding: 2rem;
  }
`;

// Add new pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
`;

const yellowPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 0, 0.7);
    border-color: rgba(255, 255, 0, 0.7);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 255, 0, 0.3);
    border-color: rgba(255, 255, 0, 1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 0, 0);
    border-color: rgba(255, 255, 0, 0.7);
  }
`;

const ScreenContent = styled.div<{ $active: boolean; $type: 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch' }>`
  color: ${props => {
    if (!props.$active) {
      if (props.$type === 'music' || props.$type === 'apps' || props.$type === 'youtube' || props.$type === 'apps2' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') return '#ffffff';
      return '#1a4a1a';
    }
    switch (props.$type) {
      case 'music': return '#ffffff';
      case 'apps': return '#ffffff';
      case 'youtube': return '#ffffff';
      case 'apps2': return '#ffffff';
      case 'nexus': return '#ffffff';
      case 'discord': return '#ffffff';
      case 'merch': return '#ffffff';
      default: return '#00ff00';
    }
  }};
  text-align: center;
  font-family: 'Share Tech Mono', monospace;
  text-shadow: ${props => {
    const color = props.$type === 'music' ? '99, 247, 230' :
                 props.$type === 'apps' ? '255, 87, 34' :
                 props.$type === 'apps2' ? '255, 87, 34' :
                 props.$type === 'youtube' ? '0, 255, 128' :
                 props.$type === 'nexus' ? '128, 147, 241' :
                 props.$type === 'discord' ? '99, 247, 230' :
                 props.$type === 'merch' ? '249, 115, 22' :
                 '0, 255, 0';
    return `
      0 0 5px rgba(${color}, 0.8),
      0 0 10px rgba(${color}, 0.5),
      0 0 15px rgba(${color}, 0.3)
    `;
  }};
  padding: ${props => {
    if (props.$type === 'music' || props.$type === 'apps' || props.$type === 'youtube' || props.$type === 'apps2' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') return '0.3rem';
    return '0.5rem';
  }};
  line-height: ${props => props.$type === 'apps' ? '1.4' : '1.2'};
  transition: all 0.3s ease;
  opacity: ${props => props.$active ? 1 : (props.$type === 'music' || props.$type === 'apps' || props.$type === 'youtube' || props.$type === 'apps2' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') ? 0.9 : 0.7};
  background: ${props => {
    const baseColor = props.$active ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.6)';
    switch (props.$type) {
      case 'music': return props.$active ? 
        'rgba(99, 247, 230, 0.7)' : 
        'rgba(99, 247, 230, 0.5)';
      case 'apps': return props.$active ? 
        'rgba(255, 87, 34, 0.7)' : 
        'rgba(255, 87, 34, 0.5)';
      case 'youtube': return props.$active ? 
        'rgba(0, 255, 128, 0.7)' : 
        'rgba(0, 255, 128, 0.5)';
      case 'apps2': return props.$active ? 
        'rgba(255, 87, 34, 0.7)' : 
        'rgba(255, 87, 34, 0.5)';
      case 'nexus': return props.$active ? 
        'rgba(128, 147, 241, 0.7)' : 
        'rgba(128, 147, 241, 0.5)';
      case 'discord': return props.$active ?
        'rgba(99, 247, 230, 0.7)' :
        'rgba(99, 247, 230, 0.5)';
      case 'merch': return props.$active ?
        'rgba(249, 115, 22, 0.7)' :
        'rgba(249, 115, 22, 0.5)';
      default: return baseColor;
    }
  }};
  width: fit-content;
  min-width: ${props => {
    if (props.$type === 'music') return '90px';
    if (props.$type === 'apps') return '124px';
    if (props.$type === 'apps2') return '80px';
    if (props.$type === 'youtube') return '60px';
    if (props.$type === 'nexus') return '40px';
    if (props.$type === 'discord') return '45px';
    if (props.$type === 'merch') return '45px';
    return '120px';
  }};
  min-height: ${props => {
    if (props.$type === 'music') return '85px';
    if (props.$type === 'apps') return '112px';
    if (props.$type === 'apps2') return '94px';
    if (props.$type === 'youtube') return '40px';
    if (props.$type === 'nexus') return '30px';
    if (props.$type === 'discord') return '45px';
    if (props.$type === 'merch') return '45px';
    return 'auto';
  }};
  max-width: ${props => {
    if (props.$type === 'music') return '90px';
    if (props.$type === 'apps') return '124px';
    if (props.$type === 'apps2') return '80px';
    if (props.$type === 'youtube') return '60px';
    if (props.$type === 'nexus') return '40px';
    if (props.$type === 'discord') return '45px';
    if (props.$type === 'merch') return '45px';
    return '200px';
  }};
  max-height: ${props => {
    if (props.$type === 'music') return '85px';
    if (props.$type === 'apps') return '112px';
    if (props.$type === 'apps2') return '94px';
    if (props.$type === 'youtube') return '40px';
    if (props.$type === 'nexus') return '30px';
    if (props.$type === 'discord') return '45px';
    if (props.$type === 'merch') return '45px';
    return 'auto';
  }};
  transform: scale(${props => {
    if (props.$type === 'music') return '0.9';
    if (props.$type === 'apps') return '0.9';
    if (props.$type === 'apps2') return '0.9';
    if (props.$type === 'youtube') return '0.9';
    if (props.$type === 'nexus') return '0.9';
    if (props.$type === 'discord') return '0.9';
    if (props.$type === 'merch') return '0.9';
    return '0.8';
  }});
  display: ${props => (props.$type === 'music' || props.$type === 'apps' || props.$type === 'apps2' || props.$type === 'youtube' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') ? 'flex' : 'block'};
  flex-direction: ${props => (props.$type === 'music' || props.$type === 'apps' || props.$type === 'apps2' || props.$type === 'youtube' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') ? 'column' : 'initial'};
  justify-content: ${props => (props.$type === 'music' || props.$type === 'apps' || props.$type === 'apps2' || props.$type === 'youtube' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') ? 'center' : 'initial'};
  align-items: ${props => (props.$type === 'music' || props.$type === 'apps' || props.$type === 'apps2' || props.$type === 'youtube' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') ? 'center' : 'initial'};
  height: ${props => (props.$type === 'music' || props.$type === 'apps' || props.$type === 'apps2' || props.$type === 'youtube' || props.$type === 'nexus' || props.$type === 'discord' || props.$type === 'merch') ? '100%' : 'auto'};
  box-shadow: ${props => {
    if (!props.$active) return 'none';
    switch (props.$type) {
      case 'music': return '0 0 15px rgba(99, 247, 230, 0.2)';
      case 'apps': return '0 0 15px rgba(255, 87, 34, 0.2)';
      case 'apps2': return '0 0 15px rgba(255, 87, 34, 0.2)';
      case 'youtube': return '0 0 15px rgba(0, 255, 128, 0.2)';
      case 'nexus': return '0 0 15px rgba(128, 147, 241, 0.2)';
      case 'discord': return '0 0 15px rgba(99, 247, 230, 0.2)';
      case 'merch': return '0 0 15px rgba(249, 115, 22, 0.2)';
      default: return 'none';
    }
  }};
  border-radius: 4px;
  border: 1px solid ${props => {
    if (!props.$active) {
      if (props.$type === 'music') return 'rgba(99, 247, 230, 0.5)';
      if (props.$type === 'apps') return 'rgba(255, 87, 34, 0.5)';
      if (props.$type === 'apps2') return 'rgba(255, 87, 34, 0.5)';
      if (props.$type === 'youtube') return 'rgba(0, 255, 128, 0.5)';
      if (props.$type === 'nexus') return 'rgba(128, 147, 241, 0.5)';
      if (props.$type === 'discord') return 'rgba(99, 247, 230, 0.5)';
      if (props.$type === 'merch') return 'rgba(249, 115, 22, 0.5)';
      return '#1a4a1a';
    }
    switch (props.$type) {
      case 'music': return '#63f7e6';
      case 'apps': return '#ff5722';
      case 'apps2': return '#ff5722';
      case 'youtube': return '#00ff80';
      case 'nexus': return '#8093f1';
      case 'discord': return '#63f7e6';
      case 'merch': return '#f97316';
      default: return '#00ff00';
    }
  }};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => {
      if (!props.$active) {
        if (props.$type === 'music') return 'linear-gradient(45deg, rgba(99, 247, 230, 0.1), transparent)';
        if (props.$type === 'apps') return 'linear-gradient(45deg, rgba(255, 87, 34, 0.1), transparent)';
        if (props.$type === 'apps2') return 'linear-gradient(45deg, rgba(255, 87, 34, 0.1), transparent)';
        if (props.$type === 'youtube') return 'linear-gradient(45deg, rgba(0, 255, 128, 0.1), transparent)';
        if (props.$type === 'nexus') return 'linear-gradient(45deg, rgba(128, 147, 241, 0.1), transparent)';
        if (props.$type === 'discord') return 'linear-gradient(45deg, rgba(99, 247, 230, 0.1), transparent)';
        if (props.$type === 'merch') return 'linear-gradient(45deg, rgba(249, 115, 22, 0.1), transparent)';
        return 'none';
      }
      switch (props.$type) {
        case 'music': return 'linear-gradient(45deg, rgba(99, 247, 230, 0.2), transparent)';
        case 'apps': return 'linear-gradient(45deg, rgba(255, 87, 34, 0.2), transparent)';
        case 'apps2': return 'linear-gradient(45deg, rgba(255, 87, 34, 0.2), transparent)';
        case 'youtube': return 'linear-gradient(45deg, rgba(0, 255, 128, 0.1), transparent)';
        case 'nexus': return 'linear-gradient(45deg, rgba(128, 147, 241, 0.1), transparent)';
        case 'discord': return 'linear-gradient(45deg, rgba(99, 247, 230, 0.2), transparent)';
        case 'merch': return 'linear-gradient(45deg, rgba(249, 115, 22, 0.2), transparent)';
        default: return 'none';
      }
    }};
    pointer-events: none;
  }

  @media (max-width: 768px) {
    width: 100% !important;
    max-width: none !important;
    min-width: none !important;
    height: 100% !important;
    transform: scale(1) !important;
    padding: 1rem !important;
  }
`;

const ScreenTitle = styled.div<{ $type: 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch' }>`
  font-size: ${props => {
    if (props.$type === 'apps') return '1rem';
    if (props.$type === 'nexus') return '0.7rem';
    if (props.$type === 'discord' || props.$type === 'merch') return '0.55rem';
    return '0.85rem';
  }};
  margin-bottom: ${props => props.$type === 'apps' ? '0.4rem' : '0.2rem'};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: bold;
`;

const ScreenText = styled.div<{ $type: 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch' }>`
  font-size: ${props => {
    if (props.$type === 'apps') return '0.9rem';
    if (props.$type === 'nexus') return '0.65rem';
    if (props.$type === 'discord' || props.$type === 'merch') return '0.5rem';
    return '0.75rem';
  }};
  opacity: 0.9;
  letter-spacing: 0.5px;
`;

const VHSOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  pointer-events: none;
  mix-blend-mode: soft-light;

  /* Scanlines - disabled */

  /* VHS tracking effect */
  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      transparent,
      rgba(0, 0, 0, 0.04) 50%,
      transparent
    );
    animation: ${vhsTrackingAnimation} 12s ease-in-out infinite;
  }
`;

const NoiseOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  pointer-events: none;
  opacity: 0.02;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  animation: ${noiseAnimation} 0.2s infinite;
  mix-blend-mode: overlay;
`;

const AspectRatioContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1920px !important;
  height: 1080px !important;
  max-width: 1920px !important;
  max-height: 1080px !important;
  min-width: 1920px !important;
  min-height: 1080px !important;
  transform-origin: center center;
  overflow: hidden !important;
  z-index: 1;
  border-radius: 32px;
  mask-image: radial-gradient(white, black);
  -webkit-mask-image: radial-gradient(white, black);
  will-change: transform;
  backface-visibility: hidden;

  @media (max-width: 768px) {
    border-radius: 0;
    height: 100vh !important;
    width: 100vw !important;
    min-width: unset !important;
    min-height: unset !important;
    transform: translate(-50%, -50%) !important;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 3;

    @media (max-width: 768px) {
      border-radius: 0;
    }
  }
`;

interface MonitorProps {
  $debug?: boolean;
  $type?: 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch';
}

const Monitor = styled(motion.div)<MonitorProps>`
  position: absolute;
  pointer-events: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 0, 0.6);
  border-radius: 4px;
  animation: ${yellowPulse} 2s infinite;
  box-shadow: 
    0 0 15px rgba(255, 255, 0, 0.3),
    0 0 30px rgba(255, 255, 0, 0.2);

  &:hover {
    transform: scale(1.05);
    border: 2px solid rgba(255, 255, 0, 0.9);
    animation: ${yellowPulse} 1s infinite;
    box-shadow: 
      0 0 25px rgba(255, 255, 0, 0.6),
      0 0 50px rgba(255, 255, 0, 0.4),
      inset 0 0 20px rgba(255, 255, 0, 0.1);

    ${ScreenContent} {
      transform: scale(1.05);
      background: ${props => {
        switch (props.$type) {
          case 'music': return 'rgba(99, 247, 230, 0.3)';
          case 'apps': return 'rgba(255, 87, 34, 0.3)';
          case 'apps2': return 'rgba(255, 87, 34, 0.3)';
          case 'youtube': return 'rgba(0, 255, 128, 0.3)';
          case 'nexus': return 'rgba(128, 147, 241, 0.3)';
          case 'discord': return 'rgba(99, 247, 230, 0.3)';
          default: return 'rgba(0, 255, 0, 0.3)';
        }
      }};
    }
  }

  @media (max-width: 768px) {
    position: relative !important;
    left: auto !important;
    top: auto !important;
    transform: none !important;
    width: 100% !important;
    max-width: 300px;
    height: auto !important;
    min-height: 120px;
  }

  ${props => props.$debug && `
    background: rgba(255, 0, 0, 0.2);
    &:hover {
      background: rgba(0, 255, 0, 0.2);
    }
  `}
`;

// Mobile brutally honest component
const MobileBrutalSite = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: #ffffff;
  color: #000000;
  font-family: Arial, sans-serif;
  padding: 20px;
  line-height: 1.6;
  text-align: center;
`;

const MobileTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 30px;
  font-weight: bold;
`;

const MobileMessage = styled.p`
  font-size: 16px;
  margin: 20px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const MobileImage = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  margin: 30px 0;
  filter: blur(2px);
  opacity: 0.7;
`;

const MobileLinks = styled.div`
  margin: 40px 0;
  text-align: left;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const MobileLink = styled.a`
  display: block;
  color: #0000EE;
  text-decoration: underline;
  margin: 10px 0;
  font-size: 16px;
  cursor: pointer;

  &:visited {
    color: #551A8B;
  }

  &:hover {
    color: #FF0000;
  }
`;

const MobileFooter = styled.p`
  margin-top: 50px;
  font-style: italic;
  color: #666666;
  font-size: 14px;
`;

const BookLaunchLink = styled.a`
  position: fixed;
  left: 1.5rem;
  bottom: 1.5rem;
  z-index: 20;
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 0.85rem;
  align-items: center;
  width: min(340px, calc(100vw - 3rem));
  padding: 0.75rem;
  border: 1px solid rgba(255, 105, 180, 0.55);
  border-radius: 6px;
  background: rgba(6, 9, 18, 0.82);
  color: #ffffff;
  text-decoration: none;
  font-family: 'Share Tech Mono', monospace;
  box-shadow:
    0 0 18px rgba(255, 105, 180, 0.18),
    inset 0 0 0 1px rgba(33, 150, 243, 0.2);
  backdrop-filter: blur(10px);
  transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #2196f3;
    box-shadow:
      0 0 24px rgba(33, 150, 243, 0.22),
      inset 0 0 0 1px rgba(255, 105, 180, 0.2);
  }

  &:after {
    display: none;
  }

  @media (max-width: 900px) {
    left: 1rem;
    bottom: 1rem;
    width: min(300px, calc(100vw - 2rem));
  }
`;

const BookThumb = styled.img`
  width: 54px;
  height: 81px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.45);
`;

const BookLaunchCopy = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const BookLaunchKicker = styled.span`
  color: #ff69b4;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BookLaunchTitle = styled.span`
  color: #ffffff;
  font-size: 1.05rem;
  line-height: 1.1;
`;

const BookLaunchMeta = styled.span`
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.82rem;
  line-height: 1.25;
`;

// Calculate initial scale before component mounts
const calculateScale = () => {
  const containerWidth = 1920;
  const containerHeight = 1080;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  
  // Don't scale on mobile
  if (windowWidth <= 768) {
    return 1;
  }
  
  const scaleX = windowWidth / containerWidth;
  const scaleY = windowHeight / containerHeight;
  return Math.min(scaleX, scaleY);
};

export const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeScreen, setActiveScreen] = useState<string | null>(null);
  const [debug, setDebug] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Initialize scale immediately with calculated value
  const [scale, setScale] = useState(calculateScale());

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const monitors = [
    {
      id: 'music',
      style: {
        left: '383px',
        top: '585px',
        width: '90px',
        height: '90px',
        transform: 'perspective(800px) rotateX(-10deg) rotateY(25deg)',
        transformOrigin: 'center center'
      },
      title: 'AUDIO',
      text: 'Initialize Sound',
      path: '/music'
    },
    {
      id: 'apps',
      style: {
        left: '565px',
        top: '500px',
        width: '123px',
        height: '110px',
        transform: 'perspective(800px) rotateX(-1deg) rotateY(33deg)',
        transformOrigin: 'center center'
      },
      title: 'IOS APPS',
      text: 'Access Projects',
      path: '/iosapps'
    },
    {
      id: 'apps2',
      style: {
        left: '742px',
        top: '504px',
        width: '75px',
        height: '95px',
        transform: 'perspective(800px) rotateX(-5deg) rotateY(25deg)',
        transformOrigin: 'center center'
      },
      title: 'WEB APPS',
      text: 'Access Projects',
      path: '/webapps'
    },
    {
      id: 'nexus',
      style: {
        left: '1066px',
        top: '620px',
        width: '40px',
        height: '30px',
        transform: 'perspective(800px) rotateX(-12deg) rotateY(-25deg)',
        transformOrigin: 'center center'
      },
      title: 'MORE',
      text: 'Links',
      path: '/nexus'
    },
    {
      id: 'merch',
      style: {
        left: '850px',
        top: '575px',
        width: '45px',
        height: '45px',
        transform: 'perspective(800px) rotateX(-5deg) rotateY(25deg)',
        transformOrigin: 'center center'
      },
      title: 'MERCH',
      text: 'Shop',
      path: '/merch'
    },
    {
      id: 'youtube',
      style: {
        left: '1318px',
        top: '628px',
        width: '60px',
        height: '40px',
        transform: 'perspective(800px) rotateX(-12deg) rotateY(-25deg)',
        transformOrigin: 'center center'
      },
      title: 'VIDEO',
      text: 'Connect',
      path: '/videos'
    }
  ];

  useEffect(() => {
    const updateScale = () => {
      setScale(calculateScale());
    };

    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'd') setDebug(prev => !prev);
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Track navigation to different sections
  const handleNavigation = (destination: string) => {
    trackEvent('main_navigation', { destination });
    navigate(destination);
  };

  // Track monitor interaction
  const handleMonitorClick = (monitorType: string) => {
    trackClick('monitor_click', { monitor_type: monitorType });
    // Existing monitor click functionality
    switch (monitorType) {
      case 'music':
        navigate('/music');
        break;
      case 'apps':
        navigate('/ios-apps');
        break;
      case 'youtube':
        navigate('/videos');
        break;
      case 'apps2':
        navigate('/web-apps');
        break;
      case 'nexus':
        navigate('/nexus');
        break;
      case 'merch':
        navigate('/merch');
        break;
      case 'cryptic':
        navigate('/hidden-transmission');
        break;
      case 'tsukikage':
        navigate('/tsukikage');
        break;
      default:
        break;
    }
  };

  // Show brutal mobile site for mobile users
  if (isMobile) {
    return (
      <>
        <Global styles={GlobalScrollbarStyles} />
        <MobileBrutalSite className="mobile-brutal-site">
        <MobileTitle>A5OMIC.COM</MobileTitle>

        <MobileMessage>
          Sorry, this site wasn't made for phones.
        </MobileMessage>

        <MobileMessage>
          Please use a real computer like an adult.
        </MobileMessage>

        <MobileImage
          src="/images/cyberpunk-room.jpg"
          alt="What you're missing on desktop"
        />

        <MobileMessage>
          A5omic is an AI-native music project. Some tracks are AI-generated,
          some are AI-assisted slowed + reverb edits, some are originals
          I've produced.
        </MobileMessage>

        <MobileMessage>
          Also, the paperback is real now: <a href="https://www.amazon.com/dp/B0H3YY738P?spcref=PRINT_LISTING" target="_blank" rel="noopener noreferrer">Brick by Brick</a>.
        </MobileMessage>

        <MobileMessage>
          But if you must...
        </MobileMessage>

        <MobileLinks>
          <MobileLink onClick={() => navigate('/music')}>
            • Music (AI-native — generated, AI-assisted, and originals)
          </MobileLink>
          <MobileLink onClick={() => navigate('/ios-apps')}>
            • iOS App Things (for your overpriced phone)
          </MobileLink>
          <MobileLink onClick={() => navigate('/web-apps')}>
            • Web App Nonsense (works on mobile I guess)
          </MobileLink>
          <MobileLink onClick={() => navigate('/build')}>
            • Build (the work — kernel, V8, WebKit, tools)
          </MobileLink>
          <MobileLink onClick={() => navigate('/merch')}>
            • Merch (Brick sample shop)
          </MobileLink>
          <MobileLink onClick={() => navigate('/nexus')}>
            • More (writing, papers, everything else)
          </MobileLink>
          <MobileLink href="https://www.amazon.com/dp/B0H3YY738P?spcref=PRINT_LISTING" target="_blank" rel="noopener noreferrer">
            • Brick by Brick (paperback on Amazon)
          </MobileLink>
          <MobileLink onClick={() => navigate('/portfolio')}>
            • Portfolio (the professional stuff)
          </MobileLink>
          <MobileLink onClick={() => navigate('/videos')}>
            • Videos I Guess (YouTube stuff)
          </MobileLink>
        </MobileLinks>

        <MobileMessage style={{ marginTop: '40px', fontSize: '18px', fontWeight: 'bold' }}>
          Fine, here's my social media:
        </MobileMessage>
        
        <MobileLinks>
          <MobileLink href="https://www.patreon.com/c/A5omic" target="_blank">
            • Patreon (early access + unreleased tracks)
          </MobileLink>
          <MobileLink href="https://discord.com/invite/GJJrgsh4Tx" target="_blank">
            • Discord (join the chaos)
          </MobileLink>
          <MobileLink href="https://x.com/A5omic" target="_blank">
            • X/Twitter (where I complain)
          </MobileLink>
          <MobileLink href="https://youtube.com/@A5omic" target="_blank">
            • YouTube (subscribe or whatever)
          </MobileLink>
          <MobileLink href="https://www.instagram.com/a5omic" target="_blank">
            • Instagram (pretty pictures)
          </MobileLink>
          <MobileLink href="https://www.tiktok.com/@a5omic" target="_blank">
            • TikTok (for the kids)
          </MobileLink>
          <MobileLink href="https://open.spotify.com/artist/7L3rQbwQVEGa71Ign85jyd" target="_blank">
            • Spotify (the catalog)
          </MobileLink>
          <MobileLink href="https://github.com/Atomics-hub" target="_blank">
            • GitHub (code and projects)
          </MobileLink>
          <MobileLink href="https://www.facebook.com/people/Tom-Ryan/100084682343532/" target="_blank">
            • Facebook (for boomers)
          </MobileLink>
          <MobileLink href="mailto:atomicmbeats@gmail.com">
            • Email: atomicmbeats@gmail.com
          </MobileLink>
        </MobileLinks>

        <MobileMessage style={{ marginTop: '30px', fontStyle: 'italic' }}>
          Pro tip: The desktop version has a secret cyberpunk room with interactive monitors. 
          But you'll never see it on that tiny screen.
        </MobileMessage>
        
        <MobileFooter>
          Seriously though, get a laptop.
        </MobileFooter>

        <MobileFooter style={{ fontSize: '12px', marginTop: '20px' }}>
          You are visitor #{Math.floor(Math.random() * 999999) + 100000}
        </MobileFooter>
        
        <MobileFooter>
          - A5omic
        </MobileFooter>
      </MobileBrutalSite>
      </>
    );
  }

  return (
    <>
      <Global styles={GlobalScrollbarStyles} />
      <CyberpunkRoom>
      <MainContainer>
        <VideoContainer>
          <BackgroundBlur>
            <BlurredVideo
              autoPlay
              loop
              muted
              playsInline
              poster="/images/cyberpunk-room.jpg"
            >
              <source src="/videos/cyberpunk-room.mp4" type="video/mp4" />
            </BlurredVideo>
          </BackgroundBlur>
          <AspectRatioContainer style={{ transform: `translate(-50%, -50%) scale(${scale})` }}>
            <ContentWrapper>
              <BackgroundVideo
                autoPlay
                loop
                muted
                playsInline
                poster="/images/cyberpunk-room.jpg"
              >
                <source src="/videos/cyberpunk-room.mp4" type="video/mp4" />
              </BackgroundVideo>
              <InteractiveArea>
                {monitors.map((monitor) => (
                  <Monitor
                    key={monitor.id}
                    style={monitor.style}
                    $debug={debug}
                    onMouseEnter={() => setActiveScreen(monitor.id)}
                    onMouseLeave={() => setActiveScreen(null)}
                    onClick={() => handleMonitorClick(monitor.id)}
                  >
                    <ScreenContent 
                      $active={activeScreen === monitor.id}
                      $type={monitor.id as 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch'}
                    >
                      <ScreenTitle $type={monitor.id as 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch'}>{monitor.title}</ScreenTitle>
                      <ScreenText $type={monitor.id as 'music' | 'apps' | 'youtube' | 'apps2' | 'nexus' | 'discord' | 'merch'}>{monitor.text}</ScreenText>
                    </ScreenContent>
                  </Monitor>
                ))}
              </InteractiveArea>
            </ContentWrapper>
          </AspectRatioContainer>
        </VideoContainer>
      </MainContainer>
      <VHSOverlay />
      <NoiseOverlay />
      <BookLaunchLink
        href="https://www.amazon.com/dp/B0H3YY738P?spcref=PRINT_LISTING"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackClick('book_launch_amazon', { source: 'home_desktop' })}
      >
        <BookThumb src="/images/books/brick-by-brick-cover.png" alt="Brick by Brick cover" />
        <BookLaunchCopy>
          <BookLaunchKicker>Paperback live</BookLaunchKicker>
          <BookLaunchTitle>Brick by Brick</BookLaunchTitle>
          <BookLaunchMeta>Read the book on Amazon &rarr;</BookLaunchMeta>
        </BookLaunchCopy>
      </BookLaunchLink>
      <SocialLinks />
    </CyberpunkRoom>
    </>
  );
};
